/**
 * Operations Projects Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjects,
  fetchProject,
  fetchProjectsStats,
  fetchMilestoneTemplates,
  createProject,
  updateProject,
  archiveProject,
  unarchiveProject,
  changeProjectStatus,
  completeMilestone,
  uncompleteMilestone,
  fetchAuditLogs,
  fetchQuotes,
  fetchQuote,
  fetchQuoteByNumber,
  fetchTakenQuoteNumbers,
} from '../api';
import type {
  ProjectsQueryParams,
  CreateProjectInput,
  UpdateProjectInput,
  ProjectStatus,
  ProjectType,
  QuotesQueryParams,
} from '../types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const projectsKeys = {
  all: ['operations-projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (params: ProjectsQueryParams) => [...projectsKeys.lists(), params] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectsKeys.details(), id] as const,
  stats: (params?: { accountId?: string; type?: ProjectType }) => [...projectsKeys.all, 'stats', params] as const,
  milestoneTemplates: (type?: string) => [...projectsKeys.all, 'milestone-templates', type] as const,
  auditLogs: (projectId: string, params?: { pageNumber?: number; pageLength?: number }) =>
    [...projectsKeys.all, 'audit-logs', projectId, params] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useProjects(params: ProjectsQueryParams = {}) {
  return useQuery({
    queryKey: projectsKeys.list(params),
    queryFn: () => fetchProjects(params),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectsKeys.detail(id!),
    queryFn: () => fetchProject(id!),
    enabled: !!id,
  });
}

export function useProjectsStats(params: { accountId?: string; type?: ProjectType } = {}) {
  return useQuery({
    queryKey: projectsKeys.stats(params),
    queryFn: () => fetchProjectsStats(params),
    staleTime: 30000, // 30 seconds
  });
}

export function useMilestoneTemplates(projectType?: string) {
  return useQuery({
    queryKey: projectsKeys.milestoneTemplates(projectType),
    queryFn: () => fetchMilestoneTemplates(projectType),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTakenQuoteNumbers(enabled = true) {
  return useQuery({
    queryKey: [...projectsKeys.all, 'taken-quote-numbers'] as const,
    queryFn: fetchTakenQuoteNumbers,
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useAuditLogs(
  projectId: string | undefined,
  params: { pageNumber?: number; pageLength?: number } = {}
) {
  return useQuery({
    queryKey: projectsKeys.auditLogs(projectId!, params),
    queryFn: () => fetchAuditLogs(projectId!, params),
    enabled: !!projectId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProjectInput }) =>
      updateProject(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.auditLogs(data.id) });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => archiveProject(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useUnarchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unarchiveProject(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useChangeProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProjectStatus }) =>
      changeProjectStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.stats() });
    },
  });
}

export function useCompleteMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, milestoneKey }: { projectId: string; milestoneKey: string }) =>
      completeMilestone(projectId, milestoneKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      // Refresh audit logs to show the milestone completion
      queryClient.invalidateQueries({ queryKey: projectsKeys.auditLogs(data.id) });
    },
  });
}

export function useUncompleteMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, milestoneKey }: { projectId: string; milestoneKey: string }) =>
      uncompleteMilestone(projectId, milestoneKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
      // Refresh audit logs to show the milestone uncomplete action
      queryClient.invalidateQueries({ queryKey: projectsKeys.auditLogs(data.id) });
    },
  });
}

// Alias for component compatibility
export function useCompleteWorkflowStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stepKey }: { id: string; stepKey: string }) =>
      completeMilestone(id, stepKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

// =============================================================================
// Quotes Query Keys & Hooks
// =============================================================================

export const quotesKeys = {
  all: ['quotes'] as const,
  lists: () => [...quotesKeys.all, 'list'] as const,
  list: (params: QuotesQueryParams) => [...quotesKeys.lists(), params] as const,
  details: () => [...quotesKeys.all, 'detail'] as const,
  detail: (id: string) => [...quotesKeys.details(), id] as const,
  byNumber: (quoteNumber: string) => [...quotesKeys.all, 'by-number', quoteNumber] as const,
};

/**
 * Hook to fetch quotes with optional filtering
 */
export function useQuotes(params: QuotesQueryParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: quotesKeys.list(params),
    queryFn: () => fetchQuotes(params),
    enabled: options.enabled !== false,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single quote by ID
 */
export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: quotesKeys.detail(id!),
    queryFn: () => fetchQuote(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch a quote by its quote number
 */
export function useQuoteByNumber(quoteNumber: string | undefined) {
  return useQuery({
    queryKey: quotesKeys.byNumber(quoteNumber!),
    queryFn: () => fetchQuoteByNumber(quoteNumber!),
    enabled: !!quoteNumber,
  });
}
